import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Chart from "react-apexcharts";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { dmmSubdimensionsResults } from "../services/api-services";
import { toastEmitter } from "../utills/utilities";
import ZoomModal from "./heatmap/zoomModal";
import Modal from "../admin/components/common/Model";
import { CHAT_ICON, MENU_ICON, ZOOM_ICON } from "../utills/image-constant";
import Remark from "../admin/components/Charts/Remark";


const DmmSubdimensionsChart = ({ selectedCompanyId, selectedCampaignId, type, graphName, title = "DMM MODEL" }) => {
  const [showTextBox, setShowTextBox] = useState(false);
  const [note, setNote] = useState("");
  const [remarkText, setRemarkText] = useState("");
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState("")
  const [isAttempt, setIsAttempt] = useState("");
  const [chartData, setChartData] = useState({ series: [], categories: [] });
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [activeDimension, setActiveDimension] = useState(null);
  const modalRef = useRef(null);

  const modalId = `dmmZoomModal-${graphName || title.replace(/\s/g, "_")}`;

  // Prepare chart options
  const options = useMemo(() => ({
    chart: { id: "DMM_Sub_Dimension", type: "bar" },
    plotOptions: { bar: { horizontal: true, dataLabels: { position: "end" } } },
    dataLabels: { enabled: true, offsetX: 25, textAnchor: "start", },
    colors: ["#557D39", "#F7BB1C", "#D42327"],
    xaxis: {
      categories: chartData.categories, style: { fontSize: '12px', colors: ["#000"], },
    },
    legend: { position: "top" },
    stroke: { width: 1.5 },
  }), [chartData.categories]);

  // Fetch data
  const getAllDmmSubdimensionsResults = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dmmSubdimensionsResults(selectedCompanyId, selectedCampaignId, type, graphName);
      if (response?.data?.responseCode === 200) {
        const data = response.data.responseBody;
        setRemarkText(response.data.remark || "");
        setRemarkId(response.data.remarkId || null);
        setIsAttempt(response.data.isAttempt ?? 0);
        setRowData(response.data);

        const categories = [];
        const importanceScores = [];
        const currentScores = [];
        const futureScores = [];

        data.forEach(dimension => {
          dimension.subDimensions.forEach(sub => {
            categories.push(`${dimension.dimensionName} (${sub.sortName})`);
            importanceScores.push(sub.importantAverageValue?.toFixed(2));
            currentScores.push(sub.currentAverageValue?.toFixed(2));
            futureScores.push(sub.futureAverageValue?.toFixed(2));
          });
        });

        setChartData({
          categories,
          series: [
            { name: "Current Score", data: currentScores },
            { name: "Future Score", data: futureScores },
            { name: "Importance Score", data: importanceScores },
          ],
        });
      } else {
        setChartData({ series: [], categories: [] });
        if (response?.data?.responseCode !== 404) toastEmitter("error", response?.data?.message);
      }
    } catch (err) {
      toastEmitter("error", "Service unavailable");
    } finally { setLoading(false); }
  }, [selectedCompanyId, selectedCampaignId, type, graphName]);

  useEffect(() => { getAllDmmSubdimensionsResults(); }, [getAllDmmSubdimensionsResults]);

  // Export functions
  const exportAsExcel = () => {
    if (!rowData || !rowData.responseBody) return;
    const title = [["DMM Sub-Dimension Results"]];
    const headers = ["Dimension", "Subdimension", "Important Avg", "Current Avg", "Future Avg"];
    const rows = [];
    rowData.responseBody?.forEach(dim => {
      dim.subDimensions?.forEach(sub => {
        rows.push([dim.dimensionName, sub.subDimensionName, sub.importantAverageValue, sub.currentAverageValue, sub.futureAverageValue]);
      });
    });
    const ws = XLSX.utils.aoa_to_sheet([...title, [], headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Subdimensions");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "DMM_Subdimension.xlsx");
  };

  const exportAsPDF = async () => {
    const el = document.getElementById("dmmChartContainer");
    if (!el) return;
    const canvas = await html2canvas(el);
    const pdf = new jsPDF("l", "mm", "a4");
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", 10, 10, 270, 140);
    pdf.save("DMM_Subdimension.pdf");
  };

  const exportAsImage = async () => {
    const el = document.getElementById("dmmChartContainer");
    if (!el) return;
    const canvas = await html2canvas(el);
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = "DMM_Subdimension.png";
    link.click();
  };

  return (
    <>

      <div id={`chart-box-${title}`} className="card h-100 ">
        <div className="d-flex justify-content-between align-items-center overall-text mb-2 p-2">
          <h5>{title}</h5>
          <div className="d-flex align-items-center">
            <button className="border-0 bg-transparent me-2" data-bs-toggle="modal" data-bs-target={`#${modalId}`}>
              <img src={ZOOM_ICON} alt="zoom" />
            </button>
            <button className="icon-btn bg-transparent" type="button" data-bs-toggle="dropdown">
              <img src={MENU_ICON} alt="menu" className="" />

            </button>
            <div className="dropdown">

              <ul className="dropdown-menu dropdown-menu-end">
                <li><button className="dropdown-item" onClick={exportAsExcel}>Download Excel</button></li>
                <li><button className="dropdown-item" onClick={exportAsPDF}>Export PDF</button></li>
                <li><button className="dropdown-item" onClick={exportAsImage}>Download Image</button></li>
              </ul>
            </div>
          </div>
        </div>

{loading ? (
          <div className="loader-container"><div className="loader"></div></div>
        ) : (
        <div id="dmmChartContainer">
      
            <Chart options={options} series={chartData.series} type="bar" height={500} />
          
        </div>
        )}
        <div className="d-flex justify-content-end my-3">
          <button
            title="Add Comment"
            onClick={() => {
              setShowRemarkModal(true);
              setTimeout(() => {
                if (modalRef.current) modalRef.current.click();
              }, 0);
            }}
            className="border-0 bg-transparent position-absolute"
            style={{
              bottom: "10px",
              right: "10px",

            }}
          >
            <img src={CHAT_ICON} alt="chat" />

          </button>
        </div>

        {/* Remark Modal */}
        {showRemarkModal && (
          <Modal modalClick={modalRef} title="Remark" buttonLable="">
            <Remark
              selectedCompanyId={selectedCompanyId}
              selectedCampaignId={selectedCampaignId}
              type={type}
              graphName={graphName}
              initialRemark={remarkText}
              remarkId={remarkId}
              isAttempt={0}
              setRemarkprops={setRemarkText}
              setRemarkIdProps={setRemarkId}
              onCloseModal={() => setShowRemarkModal(false)}
            />
          </Modal>
        )}
        <ZoomModal id={modalId} title="DMM Subdimension Zoom" 
        chartOptions={options} seriesData={chartData.series} type="bar"
         height={400} 
        remarkText={remarkText}
        setRemarkText={setRemarkText}
        showRemarkModal={showRemarkModal}
        setShowRemarkModal={setShowRemarkModal}
        modalRef={modalRef}
        exportSummaryToExcel={exportAsExcel}
        exportAsPDF={exportAsPDF}
        exportAsImage={exportAsImage}
         />
      </div>
    </>
  );
};

export default DmmSubdimensionsChart;
